import { useState } from "react";
import { Routes, Route, NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, FileText, Image as ImageIcon, Briefcase,
  Inbox, Users, ScrollText, Settings as SettingsIcon, LogOut, Menu, X, ExternalLink, Loader2, ClipboardList, Building2, BadgeCheck, FileDown,
  Home, Package, Layers, Warehouse, UserCheck, Navigation, PhoneCall,
} from "lucide-react";
import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForceChange from "./pages/ForceChange";
import Dashboard from "./pages/Dashboard";
import ProjectsList from "./pages/ProjectsList";
import ProjectEditor from "./pages/ProjectEditor";
import PagesList from "./pages/PagesList";
import PageEditor from "./pages/PageEditor";
import MediaLibrary from "./pages/MediaLibrary";
import CareersAdmin from "./pages/CareersAdmin";
import ClientsAdmin from "./pages/ClientsAdmin";
import CertsAdmin from "./pages/CertsAdmin";
import CompanyProfileAdmin from "./pages/CompanyProfileAdmin";
import CareerApplications from "./pages/CareerApplications";
import Enquiries from "./pages/Enquiries";
import AdminUsers from "./pages/Users";
import ActivityLog from "./pages/ActivityLog";
import Settings from "./pages/Settings";
import HomePageAdmin from "./pages/HomePageAdmin";
import ProductsAdmin from "./pages/ProductsAdmin";
import CapabilitiesAdmin from "./pages/CapabilitiesAdmin";
import FacilitiesAdmin from "./pages/FacilitiesAdmin";
import LeadershipAdmin from "./pages/LeadershipAdmin";
import NavigationAdmin from "./pages/NavigationAdmin";
import ContactAdmin from "./pages/ContactAdmin";

const ALL = ["super_admin", "director", "hr"];
const MENU = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, roles: ALL },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, roles: ["super_admin", "director"] },
  { to: "/admin/pages", label: "Pages", icon: FileText, roles: ["super_admin", "director"] },
  { to: "/admin/clients", label: "Clients", icon: Building2, roles: ["super_admin", "director"] },
  { to: "/admin/certifications", label: "Certifications", icon: BadgeCheck, roles: ["super_admin", "director"] },
  { to: "/admin/company-profile", label: "Company Profile", icon: FileDown, roles: ["super_admin", "director"] },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon, roles: ["super_admin", "director"] },
  { to: "/admin/home-page", label: "Home Page", icon: Home, roles: ["super_admin", "director"] },
  { to: "/admin/products", label: "Products", icon: Package, roles: ["super_admin", "director"] },
  { to: "/admin/capabilities", label: "Capabilities", icon: Layers, roles: ["super_admin", "director"] },
  { to: "/admin/facilities", label: "Facilities", icon: Warehouse, roles: ["super_admin", "director"] },
  { to: "/admin/leadership", label: "Leadership", icon: UserCheck, roles: ["super_admin", "director"] },
  { to: "/admin/navigation", label: "Navigation", icon: Navigation, roles: ["super_admin", "director"] },
  { to: "/admin/contact-info", label: "Contact Info", icon: PhoneCall, roles: ["super_admin", "director"] },
  { to: "/admin/careers", label: "Careers", icon: Briefcase, roles: ["super_admin", "hr"] },
  { to: "/admin/applications", label: "Career Applications", icon: ClipboardList, roles: ["super_admin", "hr"] },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, roles: ALL },
  { to: "/admin/users", label: "Users & Roles", icon: Users, roles: ["super_admin"] },
  { to: "/admin/activity", label: "Activity Log", icon: ScrollText, roles: ["super_admin"] },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ALL },
];

const roleLabel = { super_admin: "Super Admin", director: "Director", hr: "HR" };

const NoAccess = () => (
  <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="no-access">
    <p className="font-display text-lg font-bold text-slate-900">No access</p>
    <p className="mt-2 text-sm text-slate-500">Your role does not have permission to view this section.</p>
  </div>
);

const Shell = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-white" />
      </div>
    );
  }
  if (!user) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }
  if (user.must_change_password) return <ForceChange />;

  const guard = (roles, el) => (roles.includes(user.role) ? el : <NoAccess />);

  const nav = (
    <nav className="flex-1 overflow-y-auto py-4" data-testid="admin-nav">
      {MENU.filter((m) => m.roles.includes(user.role)).map((m) => (
        <NavLink
          key={m.to}
          to={m.to}
          end={m.end}
          onClick={() => setMenuOpen(false)}
          data-testid={`admin-nav-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-6 py-3 text-[13px] font-semibold transition-colors duration-150 ${
              isActive ? "bg-white/10 text-white border-r-2 border-[#C5221F]" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <m.icon size={16} /> {m.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-panel">
      <aside className="hidden lg:flex w-60 bg-[#0A1128] flex-col fixed inset-y-0 z-40">
        <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
          <span className="bg-[#C5221F] text-white font-display font-extrabold w-9 h-9 flex items-center justify-center text-sm">G.R</span>
          <span className="leading-tight">
            <span className="block font-display font-bold text-white text-sm">Admin Panel</span>
            <span className="block text-[10px] text-slate-500 uppercase tracking-widest">G. R. Engineering</span>
          </span>
        </div>
        {nav}
        <div className="p-4 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="admin-view-site-link">
            <ExternalLink size={12} /> View public website
          </Link>
        </div>
      </aside>

      <div className={`fixed inset-0 z-50 lg:hidden ${menuOpen ? "" : "pointer-events-none"}`}>
        <div className={`absolute inset-0 bg-[#0A1128]/60 transition-opacity duration-200 ${menuOpen ? "opacity-100" : "opacity-0"}`} onClick={() => setMenuOpen(false)} />
        <aside className={`absolute left-0 top-0 h-full w-64 bg-[#0A1128] flex flex-col transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <span className="font-display font-bold text-white text-sm">Admin Panel</span>
            <button onClick={() => setMenuOpen(false)} className="text-slate-400 p-1" data-testid="admin-menu-close-btn"><X size={20} /></button>
          </div>
          {nav}
        </aside>
      </div>

      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          <button className="lg:hidden p-2 text-slate-700" onClick={() => setMenuOpen(true)} data-testid="admin-menu-open-btn"><Menu size={22} /></button>
          <span className="hidden lg:block text-xs text-slate-400 font-mono-tech uppercase tracking-widest">Content Management · grengg.com</span>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900" data-testid="admin-user-name">{user.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#C5221F] font-bold">{roleLabel[user.role]}</p>
            </div>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F] transition-colors" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/projects" element={guard(["super_admin", "director"], <ProjectsList />)} />
            <Route path="/projects/:id" element={guard(["super_admin", "director"], <ProjectEditor />)} />
            <Route path="/pages" element={guard(["super_admin", "director"], <PagesList />)} />
            <Route path="/clients" element={guard(["super_admin", "director"], <ClientsAdmin />)} />
            <Route path="/certifications" element={guard(["super_admin", "director"], <CertsAdmin />)} />
            <Route path="/company-profile" element={guard(["super_admin", "director"], <CompanyProfileAdmin />)} />
            <Route path="/pages/:id" element={guard(["super_admin", "director"], <PageEditor />)} />
            <Route path="/media" element={guard(["super_admin", "director"], <MediaLibrary />)} />
            <Route path="/home-page" element={guard(["super_admin", "director"], <HomePageAdmin />)} />
            <Route path="/products" element={guard(["super_admin", "director"], <ProductsAdmin />)} />
            <Route path="/capabilities" element={guard(["super_admin", "director"], <CapabilitiesAdmin />)} />
            <Route path="/facilities" element={guard(["super_admin", "director"], <FacilitiesAdmin />)} />
            <Route path="/leadership" element={guard(["super_admin", "director"], <LeadershipAdmin />)} />
            <Route path="/navigation" element={guard(["super_admin", "director"], <NavigationAdmin />)} />
            <Route path="/contact-info" element={guard(["super_admin", "director"], <ContactAdmin />)} />
            <Route path="/careers" element={guard(["super_admin", "hr"], <CareersAdmin />)} />
            <Route path="/applications" element={guard(["super_admin", "hr"], <CareerApplications />)} />
            <Route path="/enquiries" element={<Enquiries />} />
            <Route path="/users" element={guard(["super_admin"], <AdminUsers />)} />
            <Route path="/activity" element={guard(["super_admin"], <ActivityLog />)} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

const AdminApp = () => (
  <AuthProvider>
    <Shell />
  </AuthProvider>
);

export default AdminApp;
